import type { Going, RaceType, RichContentRaceDetails } from '@ps/isp-web-services-types';
import { Translation } from '@ps/web-shared-components';
import React, { useRef } from 'react';

import { HorseRaceDistance, RaceStatus } from '@swf/sports/components';

import FormattedDateTime from '../FormattedDateTime/FormattedDateTime';
import QuickSwitcher from '../QuickSwitcher/QuickSwitcher';

import {
  TXTWCL_Bumper,
  TXTWCL_Chase,
  TXTWCL_Class_N0,
  TXTWCL_Dead_2,
  TXTWCL_Fast_3,
  TXTWCL_Firm,
  TXTWCL_Flat,
  TXTWCL_Frozen,
  TXTWCL_Good_4,
  TXTWCL_Good_Firm,
  TXTWCL_Good_Soft,
  TXTWCL_Hard,
  TXTWCL_Heavy,
  TXTWCL_Hurdle,
  TXTWCL_Muddy,
  TXTWCL_Sloppy,
  TXTWCL_Slow,
  TXTWCL_Soft_1,
  TXTWCL_Standard_2,
  TXTWCL_Standard_Fast,
  TXTWCL_Standard_Slow,
  TXTWCL_Wet_Fast,
  TXTWCL_Yielding
} from './RaceDetails.messages.json';
import styles from './RaceDetails.scss';

interface Props {
  details?: Pick<RichContentRaceDetails, 'distance' | 'type' | 'going' | 'raceClass' | 'status'>;
  venue?: string;
  startTime?: string;
}

const goingMap: Partial<Record<Going, { id: string; defaultMessage: string }>> = {
  DEAD: TXTWCL_Dead_2,
  FAST: TXTWCL_Fast_3,
  FIRM: TXTWCL_Firm,
  FROZEN: TXTWCL_Frozen,
  GOOD: TXTWCL_Good_4,
  GOOD_FIRM: TXTWCL_Good_Firm,
  GOOD_SOFT: TXTWCL_Good_Soft,
  HARD: TXTWCL_Hard,
  HEAVY: TXTWCL_Heavy,
  MUDDY: TXTWCL_Muddy,
  SLOPPY: TXTWCL_Sloppy,
  SLOW: TXTWCL_Slow,
  SOFT: TXTWCL_Soft_1,
  STANDARD: TXTWCL_Standard_2,
  STD: TXTWCL_Standard_2,
  STD_FAST: TXTWCL_Standard_Fast,
  STD_SLOW: TXTWCL_Standard_Slow,
  WET_FAST: TXTWCL_Wet_Fast,
  YIELDING: TXTWCL_Yielding
};

const typeMap: Record<RaceType, { id: string; defaultMessage: string }> = {
  BUMPER: TXTWCL_Bumper,
  CHASE: TXTWCL_Chase,
  FLAT: TXTWCL_Flat,
  HURDLE: TXTWCL_Hurdle
};

const RaceDetails: React.FC<Props> = ({ details, startTime, venue }) => {
  const type = details?.type;
  const distance = details?.distance;
  const going = details?.going;
  const raceClass = details?.raceClass;
  const status = details?.status;
  const goingTranslation = going && goingMap[going];
  const typeTranslation = type && typeMap[type];
  const containerRef = useRef<HTMLDivElement | null>(null);

  return (
    <div data-testid="race-details-card" ref={containerRef} className={styles.container}>
      {venue ? <QuickSwitcher title={venue} /> : null}
      <div className={styles.details}>
        {startTime ? (
          <FormattedDateTime date={startTime} format="long" weekday="long" hideTime />
        ) : null}
        {typeTranslation ? (
          <span>
            <Translation {...typeTranslation} />
          </span>
        ) : null}
        {raceClass ? (
          <span>
            <Translation values={{ 0: raceClass }} {...TXTWCL_Class_N0} />
          </span>
        ) : null}
        {distance ? <HorseRaceDistance distance={distance} /> : null}
        {goingTranslation ? (
          <span>
            <Translation {...goingTranslation} />
          </span>
        ) : null}
      </div>
      {status && (
        <div className={styles.inPlay}>
          <RaceStatus status={status} />
        </div>
      )}
    </div>
  );
};

export default RaceDetails;
